/**
 * ==========================================================================
 *  Lettering, drawn onto a canvas at build time.
 *
 *  Every word anywhere in the venue comes through here: the road signs, the
 *  notes on the tables, and the word stitched across the front of a cap - a
 *  cap being, as far as this is concerned, a very small sign hung off a head.
 *  One implementation rather than two, because a second one would be a second
 *  set of font metrics to keep matched.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import type { SignSpec } from '../world/Level';

/** Signs are canvas textures - no font assets, no licensing questions. */
export function createSign(spec: SignSpec): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = Math.max(64, Math.round((512 * spec.height) / spec.width));
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = spec.background ?? '#1b1f27';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = spec.color ?? '#f4f1e8';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
    ctx.fillStyle = spec.color ?? '#f4f1e8';
    // The font is sized off the sign's height and never shrinks to fit, so a
    // long line is authored as a wide sign. `\n` is the way out of that for the
    // one case where it cannot be: a note on a table, which is a sheet of paper
    // rather than a strip. Each line takes its share of the height, so a
    // one-line sign - i.e. every other sign on the level - is unchanged.
    const lines = spec.text.split('\n');
    const lineHeight = canvas.height / lines.length;
    ctx.font = `bold ${Math.round(lineHeight * 0.52)}px Helvetica, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    lines.forEach((line, index) => {
      ctx.fillText(line, canvas.width / 2, (index + 0.5) * lineHeight + 2);
    });
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(spec.width, spec.height),
    // FrontSide only: seen from behind a sign would render mirrored.
    new THREE.MeshBasicMaterial({ map: texture, side: THREE.FrontSide }),
  );
  mesh.position.set(spec.position.x, spec.position.y, spec.position.z);
  mesh.rotation.y = spec.rotationY;
  mesh.name = `kyltti-${spec.text.replace(/\n/g, ' ')}`;
  return mesh;
}
